import {
  Button,
  Grid,
  Loader,
  ScrollView,
  SearchField,
} from "@aws-amplify/ui-react";
import { API, graphqlOperation } from "aws-amplify";

import { useContext, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import LocalizedStrings from "react-localization";
import { FiArrowLeft, FiArrowRight } from "react-icons/fi";
import moment from "moment";
import {
  CCard,
  CCardBody,
  CCollapse,
  CModal,
  CModalBody,
  CModalHeader,
  CModalTitle,
  CTable,
} from "@coreui/react";

import { cropTypesByCompanyID, getCropUnit } from "graphql/queries";
import { updateCropType } from "graphql/mutations";
import usePermissions from "hooks/usePermission";
import CompanyBranchFilter from "components/companies/CompanyBranchFilter";
import { LayoutContext } from "layout/context/LayoutContext";
import { getCompanyName } from "graphql/custom-queries";
import ConfirmModal from "components/utils/ConfirmModal";
import ActionIcon from "components/actions/ActionIcon";
import CropTypeCreateForm from "./CropTypeCreateForm";
import CropTypeUpdateForm from "./CropTypeUpdateForm";

const CropTypes = () => {
  const { me } = useContext(LayoutContext);
  const [canCreate, canUpdate, canDelete] = usePermissions([
    "agro:read",
    "agro:read",
    "agro:read",
  ]);
  const [cropTypes, setCropTypes] = useState([]);
  const [cropTypesLoading, setCropTypesLoading] = useState(false);
  const [cropTypeDetails, setCropTypeDetails] = useState();
  const [paginationTokens, setPaginationTokens] = useState({
    previous: [],
    nextToken: null,
  });
  const [modalDetails, setModalDetails] = useState({
    visible: false,
    cropTypeId: null,
    cropTypeName: null,
  });
  const [modalCropType, setModalCropType] = useState({
    visible: false,
    cropTypeId: null,
  });
  const [searchBy, setSearchBy] = useState({
    company: {
      id: "",
      display: "",
    },
    branch: {
      id: "",
      display: "",
    },
  });
  const columnsMapped = useMemo(
    () =>
      cropTypes.map((item) => ({
        ...item,
        companyName: item.companyName || <Loader />,
        cropUnitName: item.cropUnitName || <Loader />,
        actions: (
          <>
            <ActionIcon
              name={
                cropTypeDetails?.id === item.id
                  ? strings.cropTypes.hide
                  : strings.cropTypes.show
              }
              icon={
                cropTypeDetails?.id === item.id ? "VisibilityOff" : "Visibility"
              }
              color="primary"
              onClick={() => {
                cropTypeDetails?.id === item.id
                  ? setCropTypeDetails(null)
                  : setCropTypeDetails(item);
              }}
            />
            {canUpdate && (
              <ActionIcon
                name={strings.cropTypes.edit}
                icon="Edit"
                color="primary"
                onClick={() => openModal("update", item.id)}
              />
            )}
            {canDelete && (
              <ActionIcon
                name={strings.cropTypes.delete}
                icon="Delete"
                color="red.60"
                onClick={() => {
                  setModalDetails({
                    visible: true,
                    cropTypeId: item.id,
                    cropTypeName: item.name,
                  });
                }}
              />
            )}
          </>
        ),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cropTypes, cropTypeDetails?.id]
  );

  const onNext = () => {
    fetchCropTypes(paginationTokens.nextToken);
  };
  const onPrevious = () => {
    const previous =
      paginationTokens.previous?.length <= 1
        ? null
        : paginationTokens.previous?.slice(-2)[0];
    fetchCropTypes(previous, false);
  };
  const onKeywordSubmit = (keyword) => {
    const keywordSplit = keyword && keyword.split(" ");
    fetchCropTypes(undefined, undefined, keywordSplit);
  };
  const openModal = (action, id) =>
    setModalCropType({ visible: true, action, cropTypeId: id });
  const onSuccessCropType = () => {
    setModalCropType({ visible: false });
    fetchCropTypes();
  };

  async function fetchCompanyName(id) {
    try {
      const companyData = await API.graphql(
        graphqlOperation(getCompanyName, {
          id,
        })
      );
      const companyItem = companyData.data.getCompany;
      return companyItem?.name ?? "";
    } catch (err) {
      console.log("Error fetchCompanyName", err);
      return "";
    }
  }

  async function fetchCropUnitName(id) {
    try {
      const cropUnitData = await API.graphql(
        graphqlOperation(getCropUnit, {
          id,
        })
      );
      const cropUnitItem = cropUnitData.data.getCropUnit;
      return cropUnitItem?.name ?? "";
    } catch (err) {
      console.log("Error fetchCropUnitName", err);
      return "";
    }
  }

  const getNamesByCropTypes = (list) => {
    list?.forEach(async (item) => {
      const companyName = await fetchCompanyName(item.companyID);
      const cropUnitName = await fetchCropUnitName(item.cropUnitID);

      setCropTypes((prevList) => {
        const updatedList = [...prevList];
        const index = updatedList.findIndex(
          (itemInd) => itemInd.id === item.id
        );

        if (index > -1) {
          updatedList[index] = {
            ...updatedList[index],
            companyName,
            cropUnitName,
          };
        }

        return updatedList;
      });
    });
  };

  async function fetchCropTypes(token, isNext = true, keywords) {
    const nameOperation = cropTypesByCompanyID;
    const nameOperationStr = "cropTypesByCompanyID";
    try {
      setCropTypesLoading(true);

      if (!searchBy.company.id && !me.companyID) return;

      const cropTypesData = await API.graphql(
        graphqlOperation(nameOperation, {
          companyID: searchBy.company.id || me.companyID,
          limit: !keywords ? 10 : undefined,
          nextToken: token,
          filter: keywords
            ? {
              and: keywords.map((key) => ({
                or: [
                  {
                    name: { contains: key },
                  },
                ],
              })),
              deletedAt: { attributeExists: false },
            }
            : { deletedAt: { attributeExists: false } },
        })
      );
      const cropTypesItems = cropTypesData.data[nameOperationStr].items;
      setCropTypes(cropTypesItems);
      getNamesByCropTypes(cropTypesItems);
      setPaginationTokens((prevSt) => {
        let previous = prevSt.previous ?? [];
        if (isNext) previous = token ? [...previous, token] : [...previous];
        if (!isNext && previous?.length) previous.pop();

        return {
          previous,
          nextToken: cropTypesData.data[nameOperationStr].nextToken,
        };
      });
      setCropTypesLoading(false);
    } catch (err) {
      toast.error(strings.responses.errorFetch);
      setCropTypesLoading(false);
      console.error(err);
    } finally {
      setCropTypesLoading(false);
    }
  }
  async function cropTypeDelete(id) {
    try {
      const result = await API.graphql(
        graphqlOperation(updateCropType, {
          input: { id, deletedAt: new Date().toISOString() },
        })
      );
      toast.success(strings.successDeleted);
      const deletedId = result.data.updateCropType.id;
      setCropTypes(cropTypes.filter((item) => item.id !== deletedId));
      setModalDetails({
        visible: false,
        cropTypeId: null,
      });
    } catch (err) {
      toast.error(strings.responses[err]);
    }
  }

  useEffect(() => {
    fetchCropTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchBy.branch.id, searchBy.company.id]);

  const tableColumns = [
    {
      key: "companyName",
      label: strings.table.headers.companyName,
      _style: { width: "25%" },
    },
    {
      key: "cropUnitName",
      label: strings.table.headers.cropUnitName,
      _style: { width: "25%" },
    },
    {
      key: "name",
      label: strings.table.headers.name,
      _style: { width: "25%" },
    },
    {
      key: "actions",
      label: "",
      _style: { width: "25%" },
    },
  ];

  return (
    <div>
      <span className="fs-3">{strings.title}</span>
      {canCreate && (
        <Button
          style={styles.buttonAdd}
          children={strings.cropTypes.addCropType}
          variation="primary"
          onClick={() => openModal("create")}
        />
      )}
      <div className="p-3">
        <Grid
          className="mb-3"
          templateColumns={{ medium: "1fr", xl: "1fr 1fr 1fr" }}
          rowGap="15px"
          columnGap="15px"
        >
          <CompanyBranchFilter
            searchBy={searchBy}
            setSearchBy={setSearchBy}
            onlyCompany
          />
          <SearchField
            label={strings.table.search.label}
            placeholder={strings.table.search.placeholder}
            onSubmit={onKeywordSubmit}
            onClear={onKeywordSubmit}
          />
        </Grid>
        {cropTypesLoading ? (
          <Loader />
        ) : (
          <>
            <CTable
              className="table-centered"
              items={columnsMapped}
              columns={tableColumns}
              tableHeadProps={{ color: "light" }}
              responsive
              hover
            />
            <Button
              size="small"
              disabled={paginationTokens.previous?.length <= 0}
              style={styles.buttonPrevious}
              onClick={onPrevious}
            >
              <FiArrowLeft /> {strings.table.pagination.buttonPrevious}
            </Button>
            <Button
              size="small"
              disabled={!paginationTokens.nextToken}
              style={styles.buttonNext}
              onClick={onNext}
            >
              <FiArrowRight /> {strings.table.pagination.buttonNext}
            </Button>
          </>
        )}
        <CCollapse visible={!!cropTypeDetails?.id} style={styles.collapseCard}>
          <CCard key={cropTypeDetails?.id}>
            <CCardBody>
              <h4 className="text-muted">
                {strings.details.title} "{cropTypeDetails?.name}"
              </h4>
              <p className="text-muted">
                <b>{strings.details.createdAt}:</b>{" "}
                {moment(cropTypeDetails?.createdAt).format("YYYY-MM-DD HH:mm:ss")}
              </p>
            </CCardBody>
          </CCard>
        </CCollapse>
      </div>
      <ConfirmModal
        visible={modalDetails.visible}
        body={strings.formatString(strings.modal.confirmDeleteQuestion, {
          name: modalDetails.cropTypeName,
        })}
        onCancel={() => setModalDetails({ visible: false, cropTypeId: null })}
        onConfirm={() => cropTypeDelete(modalDetails.cropTypeId)}
      />
      {modalCropType.action && (
        <CModal
          size="xl"
          alignment="center"
          visible={modalCropType.visible}
          onClose={() => setModalCropType({ visible: false })}
          backdrop="static"
        >
          <CModalHeader>
            <CModalTitle>
              {modalCropType.action === "create" && strings.modalCropType.addTitle}
              {modalCropType.action === "update" && strings.modalCropType.updateTitle}
            </CModalTitle>
          </CModalHeader>
          <CModalBody style={{ overflow: "hidden" }}>
            <ScrollView maxHeight="75vh">
              {modalCropType.action === "create" && (
                <CropTypeCreateForm onSuccess={onSuccessCropType} />
              )}
              {modalCropType.action === "update" && (
                <CropTypeUpdateForm
                  cropTypeId={modalCropType.cropTypeId}
                  onSuccess={onSuccessCropType}
                />
              )}
            </ScrollView>
          </CModalBody>
        </CModal>
      )}
    </div>
  );
};

const strings = new LocalizedStrings({
  es: {
    title: "Tipos de Cultivo",
    successDeleted: "Tipo de Cultivo eliminado con éxito",
    cropTypes: {
      addCropType: "Agregar Tipo de Cultivo",
      show: "Ver",
      hide: "Ocultar",
      edit: "Editar",
      delete: "Eliminar",
    },
    responses: {
      errorFetch: "Error al consultar los tipos de cultivo",
    },
    table: {
      headers: {
        name: "Nombre",
        companyName: "Empresa",
        cropUnitName: "Unidad de Cultivo",
        actions: "Acciones",
      },
      pagination: {
        buttonPrevious: "Atrás",
        buttonNext: "Siguiente",
      },
      search: {
        label: "Buscar",
        placeholder: "Buscar por palabra clave...",
      },
    },
    details: {
      title: "Detalles del Tipo de Cultivo",
      createdAt: "Fecha de creación",
    },
    modal: {
      confirmDeleteQuestion: "¿Estás seguro de eliminar el tipo de cultivo {name}?",
      cancelButton: "Cancelar",
      confirmButton: "Confirmar",
    },
    modalCropType: {
      addTitle: "Agregar Tipo de Cultivo",
      updateTitle: "Actualizar Tipo de Cultivo",
    },
  },
});

const styles = {
  buttonAdd: { float: "right" },
  modal: { width: "250px" },
  collapseCard: { marginTop: "15px" },
  buttonPrevious: { marginLeft: "15px" },
  buttonNext: { float: "right", marginRight: "15px" },
  searchByKeyword: { marginBottom: "15px", width: "25%", minWidth: "300px" },
};

export default CropTypes;
